import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type InlineInputType = 'text' | 'email' | 'tel';

@customElement('urban-inline-input')
export class InlineInput extends LitElement {
  static override styles = css`
    :host {
      display: block;
      margin: 8px 0;
      animation: bf-inline-in 0.22s ease-out both;
    }
    @keyframes bf-inline-in {
      from { opacity: 0; transform: translateY(-6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      :host { animation: none; }
    }
    .wrap {
      background: var(--urban-bg-soft, #1a1a1a);
      border: 1px solid var(--urban-border, #E5E7EB);
      border-left: 3px solid var(--urban-accent, #8FFE09);
      border-radius: 12px;
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .label {
      font-size: 12px;
      color: var(--urban-text, #475467);
      margin: 0;
      line-height: 1.4;
      font-weight: 600;
    }
    form {
      display: flex;
      gap: 8px;
    }
    input {
      flex: 1;
      font: inherit;
      font-size: 14px;
      padding: 9px 11px;
      border: 1px solid var(--urban-border, #262626);
      border-radius: 6px;
      background: var(--urban-bg, #0f0f0f);
      color: var(--urban-heading, #ffffff);
      caret-color: var(--urban-accent, #ff650c);
      outline: none;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    input:focus {
      border-color: var(--urban-accent, #ff650c);
      box-shadow: 0 0 0 3px var(--urban-accent-soft, rgba(255, 101, 12, 0.16));
    }
    input::placeholder { color: var(--urban-muted, #7a7a7a); }
    /* Prevent iOS Safari zoom-on-focus: inputs under 16px trigger it. Bump to
       16px on phones (<=600px); desktop keeps the 14px design. */
    @media (max-width: 600px) {
      input { font-size: 16px; }
    }
    button {
      padding: 9px 14px;
      background: var(--urban-accent, #ff650c);
      color: #0a0a0a;
      border: 2px solid var(--urban-accent, #ff650c);
      border-radius: 6px;
      font: inherit;
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    button:not(:disabled):hover {
      background: #ffffff;
      color: #0a0a0a;
      border-color: #ffffff;
    }
    button:disabled {
      background: #2a2a2a;
      border-color: #2a2a2a;
      color: var(--urban-muted, #7a7a7a);
      cursor: not-allowed;
    }
    .submitted {
      background: var(--urban-bg-soft, #F9FAFB);
      border-left: 3px solid var(--urban-accent, #8FFE09);
      padding: 12px 14px;
      border-radius: 10px;
      font-size: 13px;
      color: var(--urban-text, #475467);
    }
    .submitted strong { color: var(--urban-heading, #1D2939); }
    .hint {
      margin: 0;
      padding-top: 4px;
      border-top: 1px dashed var(--urban-border, #E5E7EB);
      font-size: 11px;
      line-height: 1.4;
      color: var(--urban-text, #475467);
    }
    .hint strong { color: var(--urban-heading, #1D2939); font-weight: 600; }
  `;

  @property({ type: String }) field = '';
  @property({ type: String }) label = '';
  @property({ type: String }) type: InlineInputType = 'text';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) prefill = '';
  @state() private value = '';
  @state() private submitted = false;

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('prefill') && this.prefill && !this.value) {
      this.value = this.prefill;
    }
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has('field')) {
      const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
      input?.focus();
    }
  }

  private valid(): boolean {
    const t = this.value.trim();
    if (!t) return false;
    if (this.type === 'email') return /^\S+@\S+\.\S+$/.test(t);
    if (this.type === 'tel') return /^[\d\s+()-]{6,}$/.test(t);
    return t.length > 0;
  }

  private submit(e: Event) {
    e.preventDefault();
    if (!this.valid()) return;
    this.submitted = true;
    this.dispatchEvent(
      new CustomEvent('inline-submit', {
        detail: { field: this.field, label: this.label, value: this.value.trim() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (this.submitted) {
      return html`<div class="submitted"><strong>Mulțumesc!</strong> Continuăm.</div>`;
    }
    return html`
      <div class="wrap">
        ${this.label ? html`<p class="label">${this.label}</p>` : ''}
        <form @submit=${this.submit}>
          <input
            type=${this.type}
            placeholder=${this.placeholder}
            .value=${this.value}
            @input=${(e: Event) => (this.value = (e.target as HTMLInputElement).value)}
            required
            autofocus
          />
          <button type="submit" ?disabled=${!this.valid()}>Trimite</button>
        </form>
        <p class="hint">
          <strong>Răspunde aici, sus.</strong> Câmpul de chat e dezactivat până completezi câmpul de mai sus.
        </p>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'urban-inline-input': InlineInput;
  }
}
